// ====================================================================
// Frontend WebSocket Logger - Para debug detalhado em produção
// ====================================================================

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}

interface WebSocketLogEntry {
  timestamp: string;
  level: LogLevel;
  category: 'WEBSOCKET';
  event: string;
  message: string;
  socketId?: string;
  authId?: string;
  eventName?: string;
  data?: any;
  error?: any;
  duration?: number;
  connectionState?: string;
  reconnectionAttempt?: number;
  latency?: number;
}

class WebSocketLogger {
  private isDevelopment: boolean;
  private isProduction: boolean;
  private logLevel: LogLevel;
  private logs: WebSocketLogEntry[] = [];
  private maxLogs: number = 1000; // Manter apenas os últimos 1000 logs
  
  constructor() {
    this.isDevelopment = (import.meta as any).env.DEV;
    this.isProduction = (import.meta as any).env.PROD;
    this.logLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.INFO;
  }

  private shouldLog(level: LogLevel): boolean {
    const levels = [LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR];
    return levels.indexOf(level) >= levels.indexOf(this.logLevel);
  }

  private createLogEntry(
    level: LogLevel,
    event: string,
    message: string,
    data?: any
  ): WebSocketLogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      category: 'WEBSOCKET',
      event,
      message,
      ...data
    };
  }

  private log(entry: WebSocketLogEntry) {
    if (!this.shouldLog(entry.level)) return;

    // Adicionar ao buffer de logs
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Log no console em desenvolvimento
    if (this.isDevelopment) {
      const prefix = `🔌 [${entry.timestamp.split('T')[1].split('.')[0]}] [WS-${entry.level.toUpperCase()}]`;
      const { timestamp, level, category, ...logData } = entry;

      switch (entry.level) {
        case LogLevel.DEBUG:
          console.debug(prefix, entry.message, logData);
          break;
        case LogLevel.INFO:
          console.info(prefix, entry.message, logData);
          break;
        case LogLevel.WARN:
          console.warn(prefix, entry.message, logData);
          break;
        case LogLevel.ERROR:
          console.error(prefix, entry.message, logData);
          break;
      }
    }

    // Em produção, enviar logs críticos para endpoint de monitoramento
    if (this.isProduction && (entry.level === LogLevel.ERROR || entry.level === LogLevel.WARN)) {
      this.sendToMonitoring(entry);
    }
  }

  private async sendToMonitoring(entry: WebSocketLogEntry) {
    try {
      // Implementar envio para serviço de monitoramento externo
      // Por exemplo: Sentry, LogRocket, DataDog, etc.
      const endpoint = (import.meta as any).env.VITE_MONITORING_ENDPOINT;
      if (endpoint) {
        await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source: 'frontend-websocket',
            environment: 'production',
            userAgent: navigator.userAgent,
            url: window.location.href,
            ...entry
          })
        });
      }
    } catch (error) {
      console.error('Failed to send log to monitoring:', error);
    }
  }

  // ====================================================================
  // Métodos públicos para logging de WebSocket
  // ====================================================================

  // Conexão estabelecida
  connected(socketId: string, authId?: string, connectionTime?: number) {
    this.log(this.createLogEntry(
      LogLevel.INFO,
      'connect',
      'WebSocket connected successfully',
      { socketId, authId: authId?.substring(0, 12) + '...', connectionTime }
    ));
  }

  // Conexão perdida
  disconnected(reason: string, authId?: string, duration?: number) {
    this.log(this.createLogEntry(
      LogLevel.WARN,
      'disconnect',
      `WebSocket disconnected: ${reason}`,
      { reason, authId: authId?.substring(0, 12) + '...', duration }
    ));
  }

  // Tentativa de reconexão
  reconnecting(attempt: number, authId?: string) {
    this.log(this.createLogEntry(
      LogLevel.INFO,
      'reconnect',
      `Attempting to reconnect (attempt ${attempt})`,
      { reconnectionAttempt: attempt, authId: authId?.substring(0, 12) + '...' }
    ));
  }

  // Evento recebido do servidor
  eventReceived(eventName: string, data?: any, authId?: string) {
    this.log(this.createLogEntry(
      LogLevel.DEBUG,
      'event_received',
      `Received event: ${eventName}`,
      {
        eventName,
        authId: authId?.substring(0, 12) + '...',
        dataSize: data ? JSON.stringify(data).length : 0,
        hasQr: !!data?.qr,
        hasChats: !!data?.chats,
        chatCount: data?.chats?.length,
        progressPercentage: data?.percentage
      }
    ));
  }

  // Evento enviado para servidor
  eventEmitted(eventName: string, data?: any, authId?: string) {
    this.log(this.createLogEntry(
      LogLevel.DEBUG,
      'event_emitted',
      `Emitted event: ${eventName}`,
      {
        eventName,
        authId: authId?.substring(0, 12) + '...',
        dataSize: data ? JSON.stringify(data).length : 0
      }
    ));
  }

  // Erro de WebSocket
  error(error: any, context?: string, authId?: string) {
    this.log(this.createLogEntry(
      LogLevel.ERROR,
      'error',
      `WebSocket error${context ? ` in ${context}` : ''}`,
      {
        error: error instanceof Error ? error.message : String(error),
        context,
        authId: authId?.substring(0, 12) + '...',
        stack: error instanceof Error ? error.stack : undefined
      }
    ));
  }

  // Timeout de conexão
  timeout(timeoutMs: number, authId?: string) {
    this.log(this.createLogEntry(
      LogLevel.WARN,
      'timeout',
      `WebSocket connection timeout (${timeoutMs}ms)`,
      { timeoutMs, authId: authId?.substring(0, 12) + '...' }
    ));
  }

  // Métricas de performance
  performance(operation: string, duration: number, authId?: string, additionalData?: any) {
    this.log(this.createLogEntry(
      LogLevel.INFO,
      'performance',
      `WebSocket performance: ${operation}`,
      {
        operation,
        duration,
        authId: authId?.substring(0, 12) + '...',
        ...additionalData
      }
    ));
  }

  // Medição de latência (ping/pong)
  latency(latencyMs: number, authId?: string) {
    this.log(this.createLogEntry(
      LogLevel.DEBUG,
      'latency',
      `WebSocket latency: ${latencyMs}ms`,
      { latency: latencyMs, authId: authId?.substring(0, 12) + '...' }
    ));
  }

  // Estado da conexão alterado
  stateChanged(oldState: string, newState: string, authId?: string) {
    this.log(this.createLogEntry(
      LogLevel.INFO,
      'state_change',
      `WebSocket state changed: ${oldState} → ${newState}`,
      {
        oldState,
        newState,
        connectionState: newState,
        authId: authId?.substring(0, 12) + '...'
      }
    ));
  }

  // ====================================================================
  // Utilitários
  // ====================================================================

  // Obter logs recentes (para debug)
  getRecentLogs(limit: number = 50): WebSocketLogEntry[] {
    return this.logs.slice(-limit);
  }

  // Limpar logs
  clearLogs() {
    this.logs = [];
  }

  // Obter estatísticas dos logs
  getStats() {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;
    const recentLogs = this.logs.filter(log => 
      new Date(log.timestamp).getTime() > oneMinuteAgo
    );

    return {
      totalLogs: this.logs.length,
      recentLogs: recentLogs.length,
      errorCount: recentLogs.filter(log => log.level === LogLevel.ERROR).length,
      warnCount: recentLogs.filter(log => log.level === LogLevel.WARN).length,
      connectionEvents: recentLogs.filter(log => 
        ['connect', 'disconnect', 'reconnect'].includes(log.event)
      ).length,
      dataEvents: recentLogs.filter(log => 
        ['event_received', 'event_emitted'].includes(log.event)
      ).length
    };
  }

  // Exportar logs para análise
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }
}

// Instância singleton do logger
export const wsLogger = new WebSocketLogger();

// Hook para React que fornece o logger e estatísticas
export const useWebSocketLogger = () => {
  return {
    logger: wsLogger,
    stats: wsLogger.getStats(),
    recentLogs: wsLogger.getRecentLogs(),
    clearLogs: () => wsLogger.clearLogs(),
    exportLogs: () => wsLogger.exportLogs()
  };
};

export default wsLogger;